import { useState, useRef } from "react";
import "@mediapipe/face_detection";
import "@mediapipe/face_mesh";
import "@tensorflow/tfjs-backend-webgl";
import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";
import { HomeModule } from "../HomeModule/HomeModule";
import { ScanModule } from "../ScanModule/ScanModule";
import { TokenUrl, AmplitudeUrl } from "../../constants";
import "./styles.css";
import { QuestionsModule } from "../QuestionsModule/QuestionsModule";
import { Results } from "../Results/Results";
import { ErrorPage } from "../ErrorPage/ErrorPage";
import { GeoBlockPage } from "../GeoBlockPage/GeoBlockPage";
import { useConfig } from "../../context/ConfigContext";
import useAiResults from "../../hooks/useAiResults";
import { useUID } from "../../context/UIDContext";
import { ProductModule } from "../ProductModule/ProductModule";

export const Pipeline = () => {
  const { aiResults, resetAiResults, fetchAiResults } = useAiResults();
  const [analysisPhase, setAnalysisPhase] = useState("home");
  const [token, updateToken] = useState("");
  const [emailPageVisited, setEmailPageVisited] = useState(false);
  const faceDetectorRef = useRef(null);
  const { uid } = useUID();
  const { configData, resetUserData } = useConfig();

  const reset = () => {
    resetAiResults();
    resetUserData();
    setEmailPageVisited(false);
  };

  const fetchUserToken = () => {
    fetch(TokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ partnerName: configData.partnerName }),
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
        throw new Error("Failed to fetch!");
      })
      .then((responseJson) => {
        updateToken(responseJson.token);
        setAnalysisPhase("scanModule");

        // save amplitude token in firebase
        fetch(AmplitudeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: responseJson.token,
          },
          body: JSON.stringify({ uuid: uid }),
        }).catch((error) => {
          console.error("Error:", error);
        });
      })
      .catch((error) => {
        setAnalysisPhase("error");
      });
  };

  const loadModels = async () => {
    if (faceDetectorRef.current) return true;
    try {
      // Load Face Detector
      const model = faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
      const detectorConfig = {
        runtime: "mediapipe",
        solutionPath: "libs/mediapipe",
      };
      faceDetectorRef.current = await faceLandmarksDetection.createDetector(
        model,
        detectorConfig,
      );
      return true;
    } catch (error) {
      return false;
    }
  };

  const runFaceModel = async (video) => {
    if (faceDetectorRef.current === null) return [];

    const faces = await faceDetectorRef.current.estimateFaces(video, {
      flipHorizontal: true,
    });
    return faces;
  };

  const renderAnalysisPhase = () => {
    switch (analysisPhase) {
      case "scanModule":
        return (
          <ScanModule
            apiToken={token}
            faceModel={runFaceModel}
            runNextModule={() => setAnalysisPhase("userData")}
            onError={() => setAnalysisPhase("error")}
          />
        );
      case "userData":
        return (
          <QuestionsModule
            apiToken={token}
            fetchAIResults={() => fetchAiResults(token)}
            runNextModule={() => setAnalysisPhase("results")}
            onError={() => setAnalysisPhase("error")}
          />
        );
      case "results":
        return (
          <Results
            results={aiResults}
            onProductClick={() => setAnalysisPhase("products")}
            onExitClick={() => setAnalysisPhase("home")}
          />
        );
      case "products":
        return (
          <ProductModule
            apiToken={token}
            aiResults={aiResults}
            showEmailPage={!emailPageVisited}
            onBackClick={() => {
              setEmailPageVisited(true);
              setAnalysisPhase("results");
            }}
            onExitClick={() => setAnalysisPhase("home")}
          />
        );
      case "error":
        return <ErrorPage onResetClick={() => setAnalysisPhase("home")} />;
      case "geoBlock":
        return <GeoBlockPage onResetClick={() => setAnalysisPhase("home")} />;
      default:
        return (
          <HomeModule
            loadModels={loadModels}
            onGeoBlock={() => setAnalysisPhase("geoBlock")}
            runNextModule={() => {
              reset();
              fetchUserToken();
            }}
          />
        );
    }
  };

  return <div className="pipeline">{renderAnalysisPhase()}</div>;
};
